import React, { useEffect } from 'react';
import { IoClose, IoMicOff, IoMic, IoVideocamOff, IoVideocam, IoCallOutline, IoSwapHorizontal } from 'react-icons/io5';

interface VideoCallModalProps {
  isOpen: boolean;
  isIncoming: boolean;
  callStatus: string;
  callTimer: number;
  otherUserName: string;
  localVideoRef: React.RefObject<HTMLVideoElement>;
  remoteVideoRef: React.RefObject<HTMLVideoElement>;
  localStream: MediaStream | null;
  onEndCall: () => void;
  onAccept?: () => void;
  onReject?: () => void;
  onToggleMute: () => void;
  onToggleVideo: () => void;
  onSwitchCamera: () => void;
  isMuted: boolean;
  isVideoOff: boolean;
  callType?: 'audio' | 'video';
  cameraFacingMode?: 'user' | 'environment';
}

const VideoCallModal: React.FC<VideoCallModalProps> = ({
  isOpen,
  isIncoming,
  callStatus,
  callTimer,
  otherUserName,
  localVideoRef,
  remoteVideoRef,
  localStream,
  onEndCall,
  onAccept,
  onReject,
  onToggleMute,
  onToggleVideo,
  onSwitchCamera,
  isMuted,
  isVideoOff,
  callType = 'video',
  cameraFacingMode = 'user',
}) => {
  useEffect(() => {
    // Attach local stream to video element
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream, localVideoRef]);

  if (!isOpen) return null;

  const formatTimer = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
      {/* Remote Video - Large Display */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black">
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className="w-full h-full object-cover"
        />
        {!remoteVideoRef.current?.srcObject && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="w-32 h-32 rounded-full bg-blue-600 mx-auto mb-4 flex items-center justify-center">
                <span className="text-4xl text-white font-bold">
                  {otherUserName.charAt(0).toUpperCase()}
                </span>
              </div>
              {/* Incoming call status */}
              {isIncoming && callStatus === 'Incoming call...' && (
                <>
                  <p className="text-white text-lg font-semibold">Incoming {callType === 'video' ? 'Video' : 'Audio'} Call</p>
                  <p className="text-gray-300 text-sm mt-1">from {otherUserName}</p>
                </>
              )}
              {/* Outgoing call status */}
              {!isIncoming && callStatus === 'Calling...' && (
                <>
                  <p className="text-white text-lg font-semibold">Calling...</p>
                  <p className="text-gray-300 text-sm mt-1">to {otherUserName}</p>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Local Video - Small PIP in Corner (only for video calls) */}
      {callType === 'video' && (
        <div className="absolute top-4 right-4 w-48 h-36 bg-gray-900 rounded-lg overflow-hidden shadow-2xl border-2 border-gray-700">
          {isVideoOff ? (
            <div className="w-full h-full bg-gray-800 flex items-center justify-center">
              <IoVideocamOff size={32} className="text-gray-500" />
            </div>
          ) : (
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className={`w-full h-full object-cover ${cameraFacingMode === 'user' ? 'scale-x-[-1]' : ''}`}
            />
          )}
        </div>
      )}

      {/* Call Info Overlay */}
      <div className="absolute top-4 left-4 bg-black bg-opacity-60 rounded-lg px-4 py-2">
        <div className="flex items-center gap-2 text-white">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-sm font-medium">{formatTimer(callTimer)}</span>
        </div>
      </div>

      {/* Call Controls - Bottom */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
        <div className="flex items-center gap-4">
          {/* Mute Toggle */}
          <button
            onClick={onToggleMute}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
              isMuted
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-gray-700 bg-opacity-80 hover:bg-opacity-100'
            } text-white`}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <IoMicOff size={24} /> : <IoMic size={24} />}
          </button>

          {/* Camera Switch */}
          {callType === 'video' && !(isIncoming && callStatus === 'Incoming call...') && (
            <button
              onClick={onSwitchCamera}
              className="w-14 h-14 rounded-full bg-gray-700 bg-opacity-80 hover:bg-opacity-100 flex items-center justify-center transition-all text-white"
              title="Switch Camera"
            >
              <IoSwapHorizontal size={24} />
            </button>
          )}

          {/* End Call / Reject */}
          {(isIncoming && callStatus === 'Incoming call...') ? (
            <>
              <button
                onClick={onReject}
                className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center text-white transition-all transform hover:scale-110"
                title="Reject"
              >
                <IoClose size={28} />
              </button>
              <button
                onClick={onAccept}
                className="w-16 h-16 rounded-full bg-green-600 hover:bg-green-700 flex items-center justify-center text-white transition-all transform hover:scale-110"
                title="Accept"
              >
                {callType === 'video' ? <IoVideocam size={28} /> : <IoCallOutline size={28} />}
              </button>
            </>
          ) : (
            <button
              onClick={onEndCall}
              className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center text-white transition-all transform hover:scale-110 shadow-lg"
              title="End Call"
            >
              <IoClose size={28} />
            </button>
          )}

          {/* Video Toggle */}
          {callType === 'video' && !(isIncoming && callStatus === 'Incoming call...') && (
            <button
              onClick={onToggleVideo}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                isVideoOff
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-gray-700 bg-opacity-80 hover:bg-opacity-100'
              } text-white`}
              title={isVideoOff ? 'Turn Video On' : 'Turn Video Off'}
            >
              {isVideoOff ? <IoVideocamOff size={24} /> : <IoVideocam size={24} />}
            </button>
          )}
        </div>
      </div>

    </div>
  );
};

export default VideoCallModal;

